import { getApiBaseUrl } from './utils';
import { getLanguageSpeechConfig } from './languageSpeechConfig';

const FIREFOX_CJK_LANGUAGE_PREFIXES = ['zh', 'ja', 'ko'];

const getSpeechSynthesisUtterance = () => {
  if (typeof window !== 'undefined' && typeof window.SpeechSynthesisUtterance !== 'undefined') {
    return window.SpeechSynthesisUtterance;
  }

  if (typeof global !== 'undefined' && typeof global.SpeechSynthesisUtterance !== 'undefined') {
    return global.SpeechSynthesisUtterance;
  }

  return null;
};

const isBrowserTtsAvailable = () => typeof window !== 'undefined' && 'speechSynthesis' in window && !!getSpeechSynthesisUtterance();

const isFirefoxLinux = () => typeof navigator !== 'undefined' && /firefox/i.test(navigator.userAgent) && /linux/i.test(navigator.userAgent);

const normalizeVoiceName = (voice) => `${voice.name || ''} ${voice.voiceURI || ''}`.toLowerCase();

const isLowQualityVoice = (voice) => {
  const normalizedName = normalizeVoiceName(voice);
  return normalizedName.includes('espeak') || normalizedName.includes('festival') || normalizedName.includes('speech dispatcher') || normalizedName.includes('speech-dispatcher') || normalizedName.includes('compact');
};

const scoreVoice = (voice, languageCode) => {
  const normalizedName = normalizeVoiceName(voice);
  let score = 0;

  if (voice.lang === languageCode) {
    score += 100;
  } else if (voice.lang && languageCode.startsWith(voice.lang.split('-')[0])) {
    score += 60;
  }

  if (voice.localService) {
    score += 20;
  }

  if (voice.default) {
    score += 10;
  }

  if (normalizedName.includes('google') || normalizedName.includes('apple') || normalizedName.includes('samsung') || normalizedName.includes('natural') || normalizedName.includes('premium') || normalizedName.includes('enhanced')) {
    score += 25;
  }

  if (normalizedName.includes('espeak') || normalizedName.includes('festival') || normalizedName.includes('compact')) {
    score -= 40;
  }

  return score;
};

const getSpeechSynthesis = () => {
  if (!isBrowserTtsAvailable()) {
    return null;
  }

  return window.speechSynthesis;
};

const getFallbackLanguageCode = (languageCode) => languageCode.split('-')[0];

const createFallbackAudio = (src) => {
  if (typeof Audio === 'undefined') {
    return null;
  }

  const audio = new Audio(src);
  audio.preload = 'auto';
  return audio;
};

export function createBrowserTtsController() {
  let currentUtterance = null;
  let currentAudio = null;
  let currentPlaybackToken = 0;
  let voiceCache = new Map();
  let warmedLanguageCode = null;
  let fallbackAudioCache = new Map();

  const getVoices = () => {
    const synthesis = getSpeechSynthesis();
    if (!synthesis) {
      return [];
    }

    const voices = synthesis.getVoices();
    return Array.isArray(voices) ? voices : [];
  };

  const resolveVoice = (languageCode) => {
    const cachedVoice = voiceCache.get(languageCode);
    if (cachedVoice) {
      return cachedVoice;
    }

    const matchingVoice = getVoices()
      .filter((voice) => voice.lang && (voice.lang === languageCode || voice.lang.startsWith(languageCode.split('-')[0])))
      .sort((a, b) => scoreVoice(b, languageCode) - scoreVoice(a, languageCode))[0] || null;

    if (matchingVoice) {
      voiceCache.set(languageCode, matchingVoice);
    }

    return matchingVoice;
  };

  const shouldUseFallbackAudio = (languageCode, voice) => {
    const languagePrefix = languageCode.split('-')[0];
    const voices = getVoices();

    if (!isFirefoxLinux() || !FIREFOX_CJK_LANGUAGE_PREFIXES.includes(languagePrefix)) {
      return false;
    }

    if (voices.length >= 1000) {
      return true;
    }

    return !voice || voice.lang !== languageCode || isLowQualityVoice(voice);
  };

  const getFallbackAudioUrl = (text, languageCode) => {
    const encodedText = encodeURIComponent(text);
    const lang = encodeURIComponent(getFallbackLanguageCode(languageCode));
    return `${getApiBaseUrl()}/api/tts/speak?text=${encodedText}&lang=${lang}`;
  };

  const preloadFallbackAudio = (text, languageCode) => {
    if (!text) {
      return null;
    }

    const cacheKey = `${languageCode}:${text}`;
    const cachedAudio = fallbackAudioCache.get(cacheKey);
    if (cachedAudio) {
      return cachedAudio;
    }

    const audio = createFallbackAudio(getFallbackAudioUrl(text, languageCode));
    if (!audio) {
      return null;
    }

    audio.load?.();
    fallbackAudioCache.set(cacheKey, audio);
    return audio;
  };

  const playFallbackAudio = ({ languageCode, onEnd, onError, onStart, playbackToken, text }) => {
    const preloadedAudio = preloadFallbackAudio(text, languageCode);
    const audio = preloadedAudio?.cloneNode ? preloadedAudio.cloneNode(true) : createFallbackAudio(getFallbackAudioUrl(text, languageCode));

    if (!audio) {
      onError?.(new Error('Fallback TTS unavailable'));
      return false;
    }

    currentAudio = audio;
    audio.onplay = () => {
      if (currentPlaybackToken !== playbackToken) {
        return;
      }
      onStart?.();
    };
    audio.onended = () => {
      if (currentPlaybackToken !== playbackToken) {
        return;
      }
      if (currentAudio === audio) {
        currentAudio = null;
      }
      onEnd?.();
    };
    audio.onerror = (event) => {
      if (currentPlaybackToken !== playbackToken) {
        return;
      }
      if (currentAudio === audio) {
        currentAudio = null;
      }
      onError?.(event);
    };

    const playPromise = audio.play?.();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch((error) => {
        if (currentPlaybackToken !== playbackToken) {
          return;
        }
        if (currentAudio === audio) {
          currentAudio = null;
        }
        onError?.(error);
      });
    }

    return true;
  };

  const stop = ({ preserveWarmup = true } = {}) => {
    currentPlaybackToken += 1;
    const synthesis = getSpeechSynthesis();
    if (!synthesis) {
      currentUtterance = null;
    } else {
      synthesis.cancel();
    }

    currentUtterance = null;
    if (currentAudio) {
      currentAudio.pause?.();
      currentAudio.currentTime = 0;
      currentAudio = null;
    }

    if (!preserveWarmup) {
      warmedLanguageCode = null;
    }
  };

  const preload = (language, text = '') => {
    const synthesis = getSpeechSynthesis();
    const { code } = getLanguageSpeechConfig(language);

    if (synthesis) {
      resolveVoice(code);

      if (warmedLanguageCode !== code) {
        warmedLanguageCode = code;
        synthesis.resume?.();
      }
    }

    if (text) {
      preloadFallbackAudio(text, code);
    }
  };

  const speak = ({ text, language, onStart, onEnd, onError }) => {
    const synthesis = getSpeechSynthesis();
    const SpeechSynthesisUtteranceCtor = getSpeechSynthesisUtterance();
    if (!text) {
      onError?.(new Error('Browser TTS unavailable'));
      return false;
    }

    const { code, rate } = getLanguageSpeechConfig(language);
    stop();
    const playbackToken = currentPlaybackToken + 1;
    currentPlaybackToken = playbackToken;
    preload(language, text);

    const voice = resolveVoice(code);
    if (!synthesis || !SpeechSynthesisUtteranceCtor || shouldUseFallbackAudio(code, voice)) {
      return playFallbackAudio({ languageCode: code, onEnd, onError, onStart, playbackToken, text });
    }

    const utterance = new SpeechSynthesisUtteranceCtor(text);
    utterance.lang = code;
    utterance.rate = rate;

    if (voice) {
      utterance.voice = voice;
    }

    utterance.onstart = () => {
      currentUtterance = utterance;
      onStart?.();
    };

    utterance.onend = () => {
      if (currentUtterance === utterance) {
        currentUtterance = null;
      }
      onEnd?.();
    };

    utterance.onerror = (event) => {
      if (currentPlaybackToken !== playbackToken) {
        return;
      }
      if (currentUtterance === utterance) {
        currentUtterance = null;
      }
      playFallbackAudio({ languageCode: code, onEnd, onError, onStart, playbackToken, text }) || onError?.(event);
    };

    currentUtterance = utterance;
    synthesis.speak(utterance);
    return true;
  };

  const bindVoiceChangeListener = () => {
    const synthesis = getSpeechSynthesis();
    if (!synthesis) {
      return () => {};
    }

    const handleVoicesChanged = () => {
      voiceCache = new Map();
    };

    if (synthesis.addEventListener) {
      synthesis.addEventListener('voiceschanged', handleVoicesChanged);
    }
    synthesis.onvoiceschanged = handleVoicesChanged;

    return () => {
      if (synthesis.removeEventListener) {
        synthesis.removeEventListener('voiceschanged', handleVoicesChanged);
      }
      if (synthesis.onvoiceschanged === handleVoicesChanged) {
        synthesis.onvoiceschanged = null;
      }
    };
  };

  return {
    bindVoiceChangeListener,
    isAvailable: isBrowserTtsAvailable,
    preload,
    speak,
    stop,
  };
}
